import { ButtonGroup, Spacer } from "@zapier/design-system";
import { tableFieldToFormField } from "server/services/tables/utils/tables-to-forms";
import { APIField } from "server/services/tables/types/fields";
import { APIRecord } from "server/services/tables/types/records";
import { FieldBlock } from "blocks/v2/Form/Field/Block";
import { Button } from "blocks/components";
import { FieldBlock as FieldBlockType } from "blocks/v2/Form/Field/types";
import { styled } from "lib/theme";
import { Fragment, useMemo } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Text } from "blocks/components/Text";

type FormData = Record<string, any>;

const FieldsWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  row-gap: 30px;
`;

const DeleteButton = styled.button`
  all: inherit;
  text-decoration: underline;
  cursor: pointer;

  transition: all 0.14159s;

  &:hover {
    color: ${({ theme }) => theme.app.colors.secondaryText};
  }
`;

export function TableRecordForm(props: {
  fields: APIField[];
  record: APIRecord | Partial<APIRecord> | undefined;
  onSubmit: (data: FormData) => void;
  isSubmitting?: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
  onCancel: () => void;
  tableId: string | undefined;
}) {
  const fieldBlocks = useMemo(
    () => getFieldBlocks(props.fields, props.tableId),
    [props.fields, props.tableId]
  );

  const defaultValues = useMemo(() => {
    if (!props.record?.data) return undefined;
    const values = Object.keys(props.record?.data).reduce((acc, cur) => {
      const fieldBlock = fieldBlocks.find(
        (f) => f.config.tablesFieldKey === cur
      );

      if (!props.record?.data?.[cur] || !fieldBlock) {
        return { ...acc, [cur]: undefined };
      }

      const config = fieldBlock.config;

      switch (config.inputType) {
        case "date-picker": {
          const date = new Date(props.record?.data[cur] as string);
          // convert to UTC if time is not included
          if (!config.includeTime)
            date.setTime(date.getTime() + date.getTimezoneOffset() * 60000);
          return { ...acc, [cur]: date };
        }
        case "dropdown": {
          // For dropdowns, values should be either string ot string array
          let value: string | string[] | undefined = undefined;
          if (config.multiSelect) {
            value = (
              props.record?.data[cur] as { label: string; value: string }[]
            ).map((item) => item?.value);
            return { ...acc, [cur]: value };
          } else {
            value = (
              props.record?.data[cur] as { label: string; value: string }
            )?.value;
          }
          return { ...acc, [cur]: value };
        }
        case "url":
          return {
            ...acc,
            [cur]: (props.record?.data[cur] as { text: string; link: string })
              .link,
          };
        default:
          return { ...acc, [cur]: props.record?.data[cur] };
      }
    }, {});
    return values;
  }, [props.record?.data, fieldBlocks]);

  const methods = useForm<FormData>({
    mode: "onBlur",
    defaultValues: defaultValues,
  });

  const renderFieldBlocks = () => {
    return fieldBlocks.map((fieldBlock, i) => (
      <FieldBlock key={i} block={fieldBlock} blockId={i.toString()} />
    ));
  };

  const onSubmit: SubmitHandler<FormData> = (data) => {
    props.onSubmit(data);
  };

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)}>
      <FormProvider {...methods}>
        <FieldsWrapper>{renderFieldBlocks()}</FieldsWrapper>
      </FormProvider>
      <Spacer height={40} />
      <ButtonGroup>
        <Button type="submit" isLoading={props.isSubmitting}>
          Save
        </Button>
        <Button type="button" color="secondary" onClick={props.onCancel}>
          Cancel
        </Button>
      </ButtonGroup>
      {props.onDelete && (
        <Fragment>
          <Spacer height={30} />
          <Text type="paragraph3">
            You can also{" "}
            <DeleteButton
              type="button"
              onClick={() => {
                props.onDelete?.();
              }}
            >
              delete this record
            </DeleteButton>
          </Text>
        </Fragment>
      )}
    </form>
  );
}

function getFieldBlocks(
  tableFields: APIField[],
  tableId: string | undefined
): FieldBlockType[] {
  const fieldBlocks: FieldBlockType[] = [];

  tableFields.forEach((tableField) => {
    try {
      const fieldBlock = tableFieldToFormField(tableField, tableId);
      if (fieldBlock)
        fieldBlocks.push({
          ...fieldBlock,
          config: {
            ...fieldBlock.config,
            name: `f${tableField.id}`,
          },
        });
    } catch (err) {
      console.error(err);
    }
  });

  return fieldBlocks;
}
